import { useEffect, useState } from 'react';
import { voidInvoice } from '@/hooks/useInvoiceAdmin';

interface DeleteInvoiceDialogProps {
  saleId: string;
  invoiceNumber: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function DeleteInvoiceDialog({
  saleId,
  invoiceNumber,
  onClose,
  onSuccess,
}: DeleteInvoiceDialogProps) {
  const [reason, setReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleConfirm = async () => {
    if (!reason.trim()) {
      setError('Debes indicar el motivo de la eliminación');
      return;
    }
    setProcessing(true);
    setError(null);
    const res = await voidInvoice(saleId, reason.trim(), 'delete');
    setProcessing(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    onSuccess();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 w-full max-w-md rounded-lg bg-background-50 border border-background-200 p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-accent-100 text-accent-800 flex items-center justify-center shrink-0">
            <i className="ri-delete-bin-6-line text-xl" />
          </div>
          <div className="min-w-0">
            <h3 className="font-heading text-lg font-semibold text-foreground-950">
              ¿Eliminar esta factura?
            </h3>
            <p className="mt-1 text-sm text-foreground-700">
              Factura <span className="font-semibold text-foreground-900">{invoiceNumber}</span>{' '}
              quedará como <span className="font-semibold">ELIMINADA</span>. No se borra físicamente,
              se conserva en auditoría y ya no contará como venta válida.
            </p>
          </div>
        </div>

        <div className="mt-4 rounded-md border border-background-200 bg-background-100/60 px-3 py-2.5 text-xs text-foreground-600 space-y-1">
          <p className="flex items-center gap-1.5">
            <i className="ri-arrow-go-back-line" /> Se restaurarán los productos al inventario de la sucursal.
          </p>
          <p className="flex items-center gap-1.5">
            <i className="ri-line-chart-line" /> Se recalcularán las ganancias y el cuadre del cajero.
          </p>
          <p className="flex items-center gap-1.5">
            <i className="ri-history-line" /> Se registrará quién, cuándo y el motivo.
          </p>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-foreground-800 mb-1.5">
            Motivo de eliminación <span className="text-accent-700">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            rows={2}
            autoFocus
            placeholder="Ej: factura duplicada, error de cobro, venta cancelada por el cliente..."
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
          />
          <p className="mt-1 text-right text-xs text-foreground-400">{reason.length}/500</p>
        </div>

        {error && (
          <div className="mt-3 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-sm text-foreground-700">
            {error}
          </div>
        )}

        <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={processing}
            className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={processing}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 hover:bg-accent-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
          >
            <i className="ri-delete-bin-6-line" />
            {processing ? 'Eliminando...' : 'CONFIRMAR ELIMINACIÓN'}
          </button>
        </div>
      </div>
    </div>
  );
}